import { useState } from 'react';
import {
  Button,
  Card,
  Badge,
  Input,
  Modal,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  useServices,
  useCreateService,
  useUpdateService,
  useDeleteService,
  type Service,
  type ServicePayload,
} from './useMasterData';

/* ── Helpers ── */

const emptyForm: ServicePayload = { name: '', code: '', description: '', unit: 'UN', price: 0 };

/* ── Component ── */

export function ServicesPage(): JSX.Element {
  const { addToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [form, setForm] = useState<ServicePayload>(emptyForm);

  const { data, isLoading, error } = useServices();

  const createMutation = useCreateService();
  const updateMutation = useUpdateService();
  const deleteMutation = useDeleteService();

  const openCreate = (): void => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (service: Service): void => {
    setEditing(service);
    setForm({
      name: service.name,
      code: service.code,
      ...(service.description ? { description: service.description } : {}),
      unit: service.unit,
      ...(service.price !== undefined ? { price: service.price } : {}),
    });
    setShowModal(true);
  };

  const handleSave = (): void => {
    if (!form.name) {
      addToast('O nome é obrigatório', 'warning');
      return;
    }

    if (editing) {
      updateMutation.mutate(
        { ...form, id: editing.id },
        {
          onSuccess: () => {
            addToast('Serviço atualizado com sucesso', 'success');
            setShowModal(false);
            setEditing(null);
          },
          onError: (err) => {
            addToast(err instanceof Error ? err.message : 'Erro ao atualizar serviço', 'error');
          },
        },
      );
    } else {
      createMutation.mutate(form, {
        onSuccess: () => {
          addToast('Serviço criado com sucesso', 'success');
          setShowModal(false);
        },
        onError: (err) => {
          addToast(err instanceof Error ? err.message : 'Erro ao criar serviço', 'error');
        },
      });
    }
  };

  const handleDelete = (id: string): void => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        addToast('Serviço excluído com sucesso', 'success');
        setDeleteConfirmId(null);
      },
      onError: (err) => {
        addToast(err instanceof Error ? err.message : 'Erro ao excluir serviço', 'error');
        setDeleteConfirmId(null);
      },
    });
  };

  return (
    <div className="md-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Serviços</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Serviços prestados e seus códigos.</p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button variant="primary" onClick={openCreate}>
          Novo Serviço
        </Button>
        </div>
      </div>

      <Card>
        {isLoading && (
          <div className="loading-container">
            <LoadingSkeleton count={6} height="32px" />
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">Erro ao carregar serviços: {(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && (!data || data.length === 0) && (
          <div className="empty-container">
            <p className="empty-message">Nenhum serviço cadastrado.</p>
            <Button variant="outline" size="sm" onClick={openCreate}>
              Cadastrar primeiro serviço
            </Button>
          </div>
        )}

        {!isLoading && !error && data && data.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.map((service) => (
                  <tr key={service.id}>
                    <td className="cell-name">{service.name}</td>
                    <td className="cell-desc" title={service.description}>
                      {service.description || '-'}
                    </td>
                    <td className="cell-value">{formatCurrency(service.price)}</td>
                    <td>
                      <Badge variant={service.status === 'ACTIVE' ? 'success' : 'neutral'}>
                        {service.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>{formatDate(service.createdAt)}</td>
                    <td>
                      <div className="row-actions">
                        <Button size="sm" variant="outline" onClick={() => openEdit(service)}>
                          Editar
                        </Button>
                        {deleteConfirmId === service.id ? (
                          <div className="inline-confirm">
                            <span className="confirm-text">Excluir?</span>
                            <Button
                              size="sm"
                              variant="danger"
                              loading={deleteMutation.isPending}
                              onClick={() => handleDelete(service.id)}
                            >
                              Sim
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteConfirmId(null)}
                              disabled={deleteMutation.isPending}
                            >
                              Não
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirmId(service.id)}
                          >
                            Excluir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        title={editing ? 'Editar Serviço' : 'Novo Serviço'}
        footer={
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => { setShowModal(false); setEditing(null); }}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!form.name || createMutation.isPending || updateMutation.isPending}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Atualizar' : 'Salvar'}
            </Button>
          </div>
        }
      >
        <div className="modal-form">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Nome do serviço"
          />
          <Input
            label="Descrição"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Descrição do serviço"
          />
          {/* `code` e obrigatorio no contrato e nao existia no formulario:
              criar servico pela tela respondia 400. */}
          <Input
            label="Código"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            placeholder="Ex.: SRV-001"
          />
          <Input
            label="Unidade"
            value={form.unit ?? ''}
            onChange={(e) => setForm({ ...form, unit: e.target.value })}
            placeholder="Ex.: UN, HORA"
          />
          <Input
            label="Valor (R$)"
            type="number"
            step="0.01"
            min="0"
            value={String(form.price ?? 0)}
            onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
          />
          {(createMutation.isError || updateMutation.isError) && (
            <p className="field-error">{(createMutation.error ?? updateMutation.error) as Error ? (createMutation.error as Error).message || (updateMutation.error as Error).message : ''}</p>
          )}
        </div>
      </Modal>

      <style>{`
        .md-page { padding: 1.5rem; }
        .loading-container { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .error-container { padding: 2rem; text-align: center; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .empty-container { padding: 2rem; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
        .empty-message { color: #74777f; font-size: 0.875rem; margin: 0; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .data-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .data-table tbody tr:hover td { background: #eceef0; }
        .cell-name { font-weight: 600; }
        .cell-desc { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #74777f; }
        .cell-value { font-weight: 600; font-variant-numeric: tabular-nums; }
        .row-actions { display: flex; gap: 0.375rem; align-items: center; }
        .inline-confirm { display: flex; align-items: center; gap: 0.375rem; }
        .confirm-text { font-size: 0.8125rem; color: #ba1a1a; font-weight: 600; margin-right: 0.25rem; }
        .modal-form { display: flex; flex-direction: column; gap: 1rem; }
        .modal-actions { display: flex; gap: 0.5rem; }
        .field-error { color: #ba1a1a; font-size: 0.8125rem; margin: 0; }
        .toggle-label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #191c1e; cursor: pointer; }
        .toggle-label input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
      `}</style>
    </div>
  );
}
